import { ref, computed } from 'vue';
import { defineStore, storeToRefs } from 'pinia';
import { useCommenting } from '@/stores/commenting/commenting';
import { useDocumentEditor } from '@/stores/document/editor';

const tiptapButtonActions = (defaultHeadingLevel) => ({
  bold: ({ chain }) => chain.toggleBold(),
  italic: ({ chain }) => chain.toggleItalic(),
  clear: ({ chain }) => chain.unsetAllMarks(),
  undo: ({ chain }) => chain.undo(),
  redo: ({ chain }) => chain.redo(),
  indent: ({ chain }) => chain.sinkListItem('listItem'),
  unindent: ({ chain }) => chain.liftListItem('listItem'),

  h1: ({ chain, state }) => (state ? chain : chain.toggleHeading({ level: 1 })),
  h2: ({ chain, state }) => (state ? chain : chain.toggleHeading({ level: 2 })),
  h3: ({ chain, state }) => (state ? chain : chain.toggleHeading({ level: 3 })),

  paragraph: ({ chain, state }) => {
    if (state) {
      return chain;
    }
    return chain
      .setParagraph()
      .setNode('paragraph', { nudgeId: null, severity: null, count: null });
  },
  heading: ({ chain, state }) => (state ? chain : chain.toggleHeading({
    level: defaultHeadingLevel,
  })),
  list: ({ chain, state }) => (state ? chain : chain.toggleBulletList()),

  bulletList: ({ chain, state }) => (state ? chain : chain.toggleBulletList()),
  orderedList: ({ chain, state }) => (state ? chain : chain.toggleOrderedList()),
});

const tiptapFlagArguments = {
  bold: ['bold'],
  italic: ['italic'],
  link: ['link'],
  heading: ['heading'],
  list: ['bulletList'],
  orderedList: ['orderedList'],
  h1: ['heading', { level: 1 }],
  h2: ['heading', { level: 2 }],
  h3: ['heading', { level: 3 }],
};

export const useDocumentEditorToolbar = defineStore('documentEditorToolbar', () => {
  const { active: currentTiptap, lastActive: lastTiptap } = storeToRefs(useDocumentEditor());
  const { showCommentsToggle, editorSelectionInCordFormat } = storeToRefs(useCommenting());
  const showLinkModal = ref(false);
  const currentLink = ref('');

  // toolbar button click or link edit modal blurs active tiptap, so action has to fallback to lastTiptap
  const tiptap = computed(() => currentTiptap.value || lastTiptap.value);

  const is = computed(() => Object.keys(tiptapFlagArguments).reduce((res, flag) => ({
    ...res,
    [flag]: Boolean(tiptap.value?.isActive(...tiptapFlagArguments[flag])),
  }), {}));

  const isEditable = computed(() => tiptap.value?.isEditable);

  // this function temporary replaces `tiptap.can().setHeading?.({ level: 1 })`
  // TODO: remove once they fix https://github.com/ueberdosis/tiptap/issues/4225
  const isHeadingLevelAvailable = (tiptapEditor, level) => {
    if (!tiptapEditor) {
      return false;
    }

    const headingExt = tiptapEditor.extensionManager.extensions
      .find((ext) => ext.name === 'heading');

    const levels = headingExt?.options.levels || [];
    return levels.includes(level);
  };

  const can = computed(() => ({
    bold: Boolean(tiptap.value?.commands.toggleBold),
    italic: Boolean(tiptap.value?.commands.toggleItalic),
    link: Boolean(tiptap.value?.commands.toggleLink),
    undo: tiptap.value?.can().undo(),
    redo: tiptap.value?.can().redo(),
    paragraph: Boolean(tiptap.value?.commands.setParagraph),
    heading: Boolean(tiptap.value?.commands.toggleHeading),

    // TODO: replace `isHeadingLevelAvailable` with `can().setHeading`
    // h1: Boolean(tiptap.value?.can().setHeading?.({ level: 1 })),
    // h2: Boolean(tiptap.value?.can().setHeading?.({ level: 2 })),
    // h3: Boolean(tiptap.value?.can().setHeading?.({ level: 3 })),
    h1: isHeadingLevelAvailable(tiptap.value, 1),
    h2: isHeadingLevelAvailable(tiptap.value, 2),
    h3: isHeadingLevelAvailable(tiptap.value, 3),

    list: Boolean(tiptap.value?.commands.toggleBulletList),
    orderedList: Boolean(tiptap.value?.commands.toggleOrderedList),
    indent: Boolean(tiptap.value?.commands.toggleBulletList)
      && tiptap.value?.can().sinkListItem('listItem'),
    unindent: Boolean(tiptap.value?.commands.toggleBulletList)
      && tiptap.value?.can().liftListItem('listItem'),
  }));

  const canChangeType = computed(() => [
    can.value.paragraph, can.value.heading, can.value.list,
  ].filter(Boolean).length > 1);

  const canClear = computed(
    () => can.value.bold || can.value.italic || can.value.link,
  );

  const nodeType = computed(() => {
    if (is.value.heading) {
      return 'heading';
    }
    if (is.value.list || is.value.orderedList) {
      return 'list';
    }
    return 'paragraph';
  });

  const noActiveFlags = computed(() => {
    const isValue = is.value || {};
    const isKeys = Object.keys(isValue);

    return isKeys.every((key) => !isValue[key]);
  });

  const onlyCanParagraphOrAdjustHistory = computed(() => {
    const canValue = can.value || {};
    const canKeys = Object.keys(canValue);

    return canKeys.every((key) => !canValue[key]
    || (['paragraph', 'redo', 'undo'].includes(key) && canValue[key]));
  });

  /**
   * Set to true when only the paragraph is being shown.
   * This is used to show disabled buttons on our default view
   */
  const isEmptyState = computed(() => nodeType.value === 'paragraph'
    && noActiveFlags.value
    && onlyCanParagraphOrAdjustHistory.value);

  const defaultHeadingLevel = computed(() => {
    if (can.value.h1) return 1;
    if (can.value.h2) return 2;
    return 3;
  });

  const propsToPass = computed(() => ({
    nodeType: nodeType.value,
    buttonState: {
      bold: is.value.bold,
      italic: is.value.italic,
      link: is.value.link,
      h1: is.value.h1,
      h2: is.value.h2,
      h3: is.value.h3,
      orderedList: is.value.orderedList,
      bulletList: is.value.list,
      comment: false,
    },
    disableAllButtons: !isEditable.value,
    disableButton: {
      typeSelect: !canChangeType.value,
      bold: !can.value.bold || isEmptyState.value,
      italic: !can.value.italic || isEmptyState.value,
      link: !can.value.link || isEmptyState.value,
      clear: !canClear.value || isEmptyState.value,
      undo: !can.value.undo,
      redo: !can.value.redo,
      indent: !can.value.indent,
      unindent: !can.value.unindent,
      orderedList: !can.value.orderedList,
      h1: !can.value.h1,
      h2: !can.value.h2,
      h3: !can.value.h3,
      comment: editorSelectionInCordFormat.value.length === 0,
    },
    hideButton: {
      paragraph: !can.value.paragraph,
      list: !can.value.list,
      heading: !can.value.heading,
      h1: !is.value.heading,
      h2: !is.value.heading,
      h3: !is.value.heading,
      indent: !is.value.list && !is.value.orderedList,
      unindent: !is.value.list && !is.value.orderedList,
      orderedList: !is.value.list && !is.value.orderedList,
      bulletList: !is.value.list && !is.value.orderedList,
      bold: is.value.heading || (!can.value.bold && !isEmptyState.value),
      italic: is.value.heading || (!can.value.italic && !isEmptyState.value),
      link: is.value.heading || (!can.value.link && !isEmptyState.value),
      clear: is.value.heading || (!canClear.value && !isEmptyState.value),
      comment: !showCommentsToggle.value,
    },
  }));

  const onLinkButtonClick = () => {
    showLinkModal.value = true;
    const previousLink = tiptap.value?.getAttributes('link').href;
    if (previousLink) {
      currentLink.value = previousLink;
    }
  };

  const onUpdateLink = ({ link }) => {
    if (link === null) {
      return;
    }

    // empty
    if (link === '') {
      tiptap.value
        .chain()
        .focus()
        .extendMarkRange('link')
        .unsetLink()
        .run();

      showLinkModal.value = false;
      currentLink.value = link;
      return;
    }

    // update link
    tiptap.value
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href: link })
      .run();

    currentLink.value = '';
    showLinkModal.value = false;
  };

  const onButtonClick = ({ id, state }) => {
    if (!tiptap.value) {
      return;
    }

    if (!tiptap.value.isEditable) {
      return;
    }

    if (tiptapButtonActions(defaultHeadingLevel.value)[id]) {
      const fn = tiptapButtonActions(defaultHeadingLevel.value)[id];
      const chain = tiptap.value.chain().focus();
      fn({ chain, state }).run();
      return;
    }

    if (id === 'link') {
      onLinkButtonClick();
      return;
    }

    throw new Error(`Unknown button action: "${id}" state: ${state}`);
  };

  const eventHandlers = computed(() => ({
    buttonClick: onButtonClick,
    commentClick: () => useCommenting().commentSelection(),
    updateLink: onUpdateLink,
  }));

  return {
    showLinkModal, currentLink, propsToPass, eventHandlers, onLinkButtonClick,
  };
});
